import type { TemplateOut, TemplatePreviewOut } from "@sagot/api-client";
import { TEMPLATE_ICONS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Icon, Skeleton, cn } from "@sagot/ui";
import type { ReactNode } from "react";
import { isAlwaysOn } from "./setup-config";
import { fillOrgName } from "./ranking";

const TITLE_ID = "template-details-title";
const AUDIENCE_CODES = [1, 2, 3, 4] as const;
const SKELETON_ROWS = 4;

function Block({ title, className, children }: { title: string; className?: string; children: ReactNode }) {
  return (
    <div className={cn("min-w-0", className)}>
      <h4 className="mb-2 text-xs font-bold text-fg-subtle">{title}</h4>
      {children}
    </div>
  );
}

/** Same grid and block heights as `DetailsBody`, so the content swaps in without moving the page. */
function DetailsSkeleton() {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2" data-testid="template-details-skeleton">
      <Block title={t("onboarding.org.setsUpAssistant")}>
        <Skeleton className="h-5 w-40" />
        <Skeleton className="mt-2 h-4 w-full" />
        <Skeleton className="mt-1.5 h-4 w-full" />
        <Skeleton className="mt-1.5 h-4 w-2/3" />
      </Block>
      <Block title={t("onboarding.org.setsUpStarters")}>
        <div className="space-y-2">
          {Array.from({ length: SKELETON_ROWS }, (_, i) => (
            <Skeleton key={i} className="h-7 w-full rounded-md" />
          ))}
        </div>
      </Block>
      <Block title={t("onboarding.org.setsUpAudiences")}>
        <div className="flex flex-wrap gap-2">
          <Skeleton className="h-6 w-20 rounded-pill" />
          <Skeleton className="h-6 w-24 rounded-pill" />
        </div>
      </Block>
      <Block title={t("onboarding.org.setsUpPipeline")}>
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: SKELETON_ROWS + 1 }, (_, i) => (
            <Skeleton key={i} className="h-6 w-20 rounded-pill" />
          ))}
        </div>
      </Block>
      <Block title={t("onboarding.org.setsUpActions")} className="md:col-span-2">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-10 w-full rounded-lg" />
          ))}
        </div>
      </Block>
    </div>
  );
}

function DetailsBody({ preview, orgName }: { preview: TemplatePreviewOut; orgName: string }) {
  const { t } = useTranslation();
  const served = AUDIENCE_CODES.filter((code) => preview.audiences.includes(code) && preview.audience_labels[code - 1]);
  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2" data-testid="template-details-body">
      <Block title={t("onboarding.org.setsUpAssistant")}>
        <p className="text-sm font-bold text-fg">{preview.assistant_name}</p>
        <p className="mt-1 line-clamp-4 text-sm text-fg-muted">{fillOrgName(preview.persona, orgName)}</p>
      </Block>
      <Block title={t("onboarding.org.setsUpStarters")}>
        <ul className="space-y-2">
          {preview.starter_questions.map((question) => (
            <li key={question} className="flex items-start gap-2 rounded-md bg-sunken px-3 py-1.5 text-sm text-fg">
              <Icon name="message-circle" className="mt-0.5 size-4 shrink-0 text-fg-subtle" />
              {question}
            </li>
          ))}
        </ul>
      </Block>
      <Block title={t("onboarding.org.setsUpAudiences")}>
        <ul className="flex flex-wrap gap-2">
          {served.map((code) => (
            <li key={code}>
              <Badge tone="brand">
                <Icon name="users" className="size-3.5" />
                {preview.audience_labels[code - 1]}
              </Badge>
            </li>
          ))}
        </ul>
      </Block>
      <Block title={t("onboarding.org.setsUpPipeline")}>
        <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-2">
          {preview.lead_stages.map((stage, index) => (
            <li key={stage.name} className="inline-flex items-center gap-1.5">
              {index > 0 ? <Icon name="chevron-right" className="size-3.5 text-fg-subtle" /> : null}
              <Badge tone={stage.is_won ? "success" : stage.is_lost ? "danger" : "neutral"}>
                {stage.is_won ? <Icon name="check-circle" className="size-3.5" /> : null}
                {stage.is_lost ? <Icon name="x-circle" className="size-3.5" /> : null}
                {stage.name}
                {stage.is_won ? <span className="sr-only"> ({t("onboarding.configure.stageWon")})</span> : null}
                {stage.is_lost ? <span className="sr-only"> ({t("onboarding.configure.stageLost")})</span> : null}
              </Badge>
            </li>
          ))}
        </ol>
      </Block>
      <Block title={t("onboarding.org.setsUpActions")} className="md:col-span-2">
        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {preview.actions.map((action) => (
            <li key={action.key} className={cn("flex items-center gap-2 rounded-lg border border-line px-3 py-2 text-sm", !action.enabled && "text-fg-muted")}>
              <Icon name={action.enabled ? "check-circle" : "minus"} className={cn("size-4 shrink-0", action.enabled ? "text-success" : "text-fg-subtle")} />
              <span className="min-w-0 flex-1 font-semibold">{action.name}</span>
              {isAlwaysOn(action.key) ? (
                <Badge tone="neutral">{t("onboarding.configure.alwaysOn")}</Badge>
              ) : (
                <span className="text-xs text-fg-subtle">{action.enabled ? t("onboarding.org.actionOn") : t("onboarding.org.actionOff")}</span>
              )}
            </li>
          ))}
        </ul>
      </Block>
    </div>
  );
}

interface TemplateDetailsProps {
  template: TemplateOut;
  preview: TemplatePreviewOut | undefined;
  /** First load of this template (nothing to show yet) → skeleton. */
  isPending: boolean;
  /** Refreshing the same template (goals changed) → content stays, marked busy. */
  isRefreshing: boolean;
  isError: boolean;
  isRecommended: boolean;
  orgName: string;
  onRetry: () => void;
}

/**
 * "What this template sets up" — loaded from the server when a template is selected (skeleton
 * with the final layout while loading). A polite live region announces when details are ready.
 */
export function TemplateDetails({ template, preview, isPending, isRefreshing, isError, isRecommended, orgName, onRetry }: TemplateDetailsProps) {
  const { t } = useTranslation();
  const loading = isPending && !isError;
  const status = isError
    ? t("onboarding.org.templateDetailsError")
    : loading
      ? t("onboarding.org.templateLoading", { template: template.name })
      : t("onboarding.org.templateLoaded", { template: template.name });
  return (
    <section className="card" aria-labelledby={TITLE_ID} aria-busy={loading || isRefreshing} data-testid="template-details" data-template-details={template.key}>
      <div className="card-body space-y-4">
        <header className="flex flex-wrap items-center gap-2">
          <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-primary-soft text-primary-ink">
            <Icon name={TEMPLATE_ICONS[template.key] ?? "sparkles"} className="size-4" />
          </span>
          <h3 id={TITLE_ID} className="text-base font-bold">
            {t("onboarding.org.setsUp", { template: template.name })}
          </h3>
          {isRecommended ? <Badge tone="brand">{t("onboarding.org.recommended")}</Badge> : <Badge tone="neutral">{t("onboarding.org.yourChoice")}</Badge>}
        </header>
        {isError ? (
          <div className="flex flex-wrap items-center gap-3 text-sm text-warning" role="alert">
            <p>{t("onboarding.org.templateDetailsError")}</p>
            <Button type="button" variant="secondary" size="sm" onClick={onRetry}>
              <Icon name="refresh-cw" />
              {t("onboarding.org.retry")}
            </Button>
          </div>
        ) : loading || !preview ? (
          <DetailsSkeleton />
        ) : (
          <div className={cn("animate-fade-in motion-reduce:animate-none", isRefreshing && "opacity-60 transition-opacity motion-reduce:transition-none")}>
            <DetailsBody preview={preview} orgName={orgName} />
            <p className="mt-4 text-xs text-fg-subtle">{t("onboarding.org.setsUpFootnote")}</p>
          </div>
        )}
        <p className="sr-only" role="status" aria-live="polite">
          {status}
        </p>
      </div>
    </section>
  );
}
